import { ChevronDown } from "@tamagui/lucide-icons";
import { useContext, useEffect, useId, useRef, useState } from "react";
import { Easing, type ImageSourcePropType, View as RNView } from "react-native";
import Animated, {
  AnimatedRef,
  SharedValue,
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  withTiming,
} from "react-native-reanimated";
import { XStack, YStack, getTokenValue, useTheme } from "tamagui";

import Image, { useImageAspectRatio } from "@/components/Image";
import { Button } from "./Button";
import { LayoutContext } from "./LayoutProvider";
import { Text } from "./Typography";

export interface AccordionDrawerProps {
  heading: string;
  body: string;
  imageSource: ImageSourcePropType;
}

const DRAWER_DURATION = 15000;

const AnimatedBar = ({
  index,
  proportion,
  toggled,
}: {
  index: number;
  proportion: SharedValue<number>;
  toggled: number;
}) => {
  const theme = useTheme();

  const derivedProportion = useDerivedValue(() => {
    if (toggled !== index) return 0;
    return proportion.value * 100;
  }, [proportion, toggled, index]);

  const style = useAnimatedStyle(() => {
    return {
      width: `${derivedProportion.value}%`,
      backgroundColor: theme.fillPrimary?.get(),
      height: toggled === index ? 4 : 1,
    };
  }, [proportion, theme, toggled, index]);

  return (
    <XStack width="100%" backgroundColor="$contentLoaderForeground">
      <Animated.View style={style} />
    </XStack>
  );
};

const Drawer = ({
  index,
  heading,
  body,
  imageSource,
  onToggle,
  toggled,
  animatedBarProportion,
}: AccordionDrawerProps & {
  toggled: number;
  index: number;
  onToggle: () => void;
  animatedBarProportion: SharedValue<number>;
}) => {
  const panelId = useId();
  const isOpen = toggled === index;
  const { lg } = useContext(LayoutContext);

  const height = useSharedValue(0);
  const derivedHeight = useDerivedValue(
    () =>
      withTiming(Number(toggled === index) * height.value, {
        duration: 300,
      }),
    [toggled, height, index],
  );
  const heightStyle = useAnimatedStyle(() => {
    return {
      height: derivedHeight.value,
    };
  }, [derivedHeight]);

  const rotation = useSharedValue(0);
  const derivedRotation = useDerivedValue(() => {
    return withTiming(Number(toggled === index) * 180, {
      duration: 300,
    });
  }, [toggled, index, rotation]);
  const rotationStyle = useAnimatedStyle(() => {
    return {
      transform: [{ rotate: `${derivedRotation.value}deg` }],
    };
  }, [derivedRotation]);

  return (
    <YStack>
      <AnimatedBar
        toggled={toggled}
        index={index}
        proportion={animatedBarProportion}
      />
      <Button.Wrapper
        paddingVertical="$250"
        flexDirection="row"
        onPress={onToggle}
        dataTrackingId="accordion-toggle"
        aria-label={`Toggle ${heading}`}
        aria-expanded={isOpen}
        aria-controls={panelId}
      >
        <Text size="$lg" medium flexGrow={1}>
          {heading}
        </Text>
        <Animated.View style={rotationStyle}>
          <ChevronDown flexShrink={0} />
        </Animated.View>
      </Button.Wrapper>
      <Animated.View
        id={panelId}
        style={[heightStyle, { overflow: "hidden" }]}
        aria-hidden={!isOpen}
      >
        <YStack
          onLayout={(e) => {
            height.value = e.nativeEvent.layout.height;
          }}
        >
          <Text marginBottom="$250">{body}</Text>
          {!lg && (
            <Image
              width="100%"
              height={275}
              objectFit="contain"
              source={imageSource}
              borderRadius="$200"
              marginBottom="$250"
            />
          )}
        </YStack>
      </Animated.View>
    </YStack>
  );
};

interface AccordionProps {
  drawers: AccordionDrawerProps[];
  scrollRef: AnimatedRef<Animated.ScrollView>;
  scrollY: number;
}

export default function AutoplayAccordion({
  drawers,
  scrollRef,
  scrollY,
}: AccordionProps) {
  const { lg } = useContext(LayoutContext);
  const [toggled, setToggled] = useState(0);
  const { aspectRatio: imageAspectRatio, onLoad: onImageLoad } =
    useImageAspectRatio();
  const animatedBarProportion = useSharedValue(0);
  const [startAnimating, setStartAnimating] = useState(false);

  const accordionRef = useRef<RNView | null>(null);
  const accordionYPosition = useRef(0);
  const drawerChangeInterval = useRef<ReturnType<typeof setInterval>>(null);

  useEffect(() => {
    const interval = setInterval(() => {
      accordionRef.current?.measureLayout(scrollRef?.current as any, (x, y) => {
        accordionYPosition.current = y;
      });
    }, 500);
    return () => clearInterval(interval);
  }, [scrollRef]);

  useEffect(() => {
    if (scrollY > accordionYPosition.current) setStartAnimating(true);
  }, [scrollY]);

  useEffect(() => {
    if (!startAnimating) return;
    animatedBarProportion.value = 0;
    animatedBarProportion.value = withTiming(1, {
      duration: DRAWER_DURATION,
      easing: Easing.linear,
    });
    drawerChangeInterval.current = setInterval(() => {
      setToggled(((toggled || 0) + 1) % drawers.length);
      animatedBarProportion.value = 0;
      animatedBarProportion.value = withTiming(1, {
        duration: DRAWER_DURATION,
        easing: Easing.linear,
      });
    }, DRAWER_DURATION);
    return () => {
      if (drawerChangeInterval.current) {
        clearInterval(drawerChangeInterval.current);
      }
    };
  }, [drawers.length, toggled, animatedBarProportion, startAnimating]);

  const renderedDrawers = drawers.map((drawer, idx) => (
    <Drawer
      animatedBarProportion={animatedBarProportion}
      index={idx}
      key={drawer.heading}
      heading={drawer.heading}
      body={drawer.body}
      imageSource={drawer.imageSource}
      onToggle={() => {
        if (toggled !== idx) {
          setToggled(idx);
          animatedBarProportion.value = 0;
          animatedBarProportion.value = withTiming(1, {
            duration: DRAWER_DURATION,
            easing: Easing.linear,
          });
          if (drawerChangeInterval.current) {
            clearInterval(drawerChangeInterval.current);
          }
          drawerChangeInterval.current = setInterval(() => {
            setToggled(((toggled || 0) + 1) % drawers.length);
            animatedBarProportion.value = 0;
            animatedBarProportion.value = withTiming(1, {
              duration: DRAWER_DURATION,
              easing: Easing.linear,
            });
          }, DRAWER_DURATION);
        }
      }}
      toggled={toggled}
    />
  ));

  if (!lg) {
    return (
      <RNView
        style={{ display: "flex", flexDirection: "column" }}
        ref={accordionRef}
      >
        {renderedDrawers}
      </RNView>
    );
  }

  return (
    <RNView
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "row",
        gap: getTokenValue("$800"),
      }}
      ref={accordionRef}
    >
      <Image
        source={drawers[toggled]?.imageSource}
        flex={1}
        maxHeight={400}
        aspectRatio={imageAspectRatio}
        borderRadius="$250"
        onLoad={onImageLoad}
      />
      <YStack width="50%">{renderedDrawers}</YStack>
    </RNView>
  );
}
